'use client';

import { useMergeRefs } from '@floating-ui/react';
import { IconMoreFilled } from '@zillow/constellation-icons/react/filled/icon-more-filled';
import { type ComponentProps, forwardRef, useContext, useMemo } from 'react';

import { cx } from '@/styled-system/css';

import { IconButton } from '../icon-button';
import { Slot } from '../slot';
import { MenuRootContext } from './context/menu-root.context';
import type { MenuTriggerPropsInterface } from './types/menu-trigger.types';

/**
 * @since 10.0.0
 */
export const MenuTrigger = forwardRef<HTMLButtonElement, MenuTriggerPropsInterface>(
  (props: MenuTriggerPropsInterface, forwardedRef) => {
    const { children, css: cssProp, ...rest } = props;
    const { menu } = useContext(MenuRootContext);
    const mergedRefs = useMergeRefs([menu.refs.setReference, forwardedRef]);

    const commonProps = useMemo(() => {
      const mergedReferenceProps = menu.getReferenceProps(rest) as ComponentProps<'button'>;
      const { 'aria-haspopup': _ariaHasPopup, ...referenceProps } = mergedReferenceProps;

      /*
       * Floating UI's useRole only sets aria-controls while open. Always pin it to the
       * stable contentId so closed triggers still advertise the controlled panel.
       */
      return {
        'ref': mergedRefs,
        'data-c11n-version': __C11N_VERSION__,
        'data-c11n-component': 'Menu.Trigger',
        ...referenceProps,
        'aria-controls': menu.contentId,
        'className': cx('c11n_menu__trigger', referenceProps.className),
      };
    }, [mergedRefs, menu, rest]);

    if (children) {
      return <Slot {...commonProps}>{children}</Slot>;
    }

    return (
      <IconButton
        {...commonProps}
        icon={<IconMoreFilled />}
        emphasis="bare"
        tone="brand"
        title="More"
        css={cssProp}
      />
    );
  },
);

MenuTrigger.displayName = 'Menu.Trigger';
